import { iso6393 } from "iso-639-3";

/** ISO 639-3 → 条目（启动时建索引） */
export type Iso6393Entry = (typeof iso6393)[number];

let byIso6393: Map<string, Iso6393Entry> | null = null;
let byIso6391: Map<string, Set<string>> | null = null;
let zhNameToSubtag: Map<string, string> | null = null;

function normStr(s: string): string {
  return s
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "")
    .replace(/（[^）]*）/g, "")
    .replace(/\([^)]*\)/g, "");
}

function ensureIndexes(): void {
  if (byIso6393) return;
  const m3 = new Map<string, Iso6393Entry>();
  const m1 = new Map<string, Set<string>>();
  for (const lang of iso6393) {
    m3.set(lang.iso6393.toLowerCase(), lang);
    if (lang.iso6391) {
      const k = lang.iso6391.toLowerCase();
      let set = m1.get(k);
      if (!set) {
        set = new Set();
        m1.set(k, set);
      }
      set.add(lang.iso6393);
    }
  }
  byIso6393 = m3;
  byIso6391 = m1;
}

/** 浏览器 Intl：用语种中文名反查 BCP47 主码（适配 ERP 纯数字 value） */
function ensureZhNameIndex(): void {
  if (zhNameToSubtag) return;
  zhNameToSubtag = new Map<string, string>();
  if (typeof Intl === "undefined" || typeof (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf !== "function") {
    return;
  }
  try {
    const supported = (Intl as unknown as { supportedValuesOf: (k: string) => string[] }).supportedValuesOf("language");
    const dn = new Intl.DisplayNames(["zh-Hans"], { type: "language" });
    for (const tag of supported) {
      const base = tag.split("-")[0]?.toLowerCase();
      if (!base) continue;
      try {
        const label = dn.of(tag) ?? dn.of(base);
        if (!label) continue;
        const key = normStr(label);
        if (key && !zhNameToSubtag.has(key)) zhNameToSubtag.set(key, base);
      } catch {
        // ignore invalid tag
      }
    }
  } catch {
    zhNameToSubtag = new Map();
  }
}

/**
 * 将字典项解析为一组可能的 ISO 639-3（同一 ERP 语种可能对应多个标准码）。
 */
export function dictLangToIso6393Candidates(value: string, name: string): Set<string> {
  ensureIndexes();
  ensureZhNameIndex();
  const out = new Set<string>();
  const raw = String(value ?? "").trim();
  const v = raw.replace(/_/g, "-").toLowerCase();

  if (v && byIso6393!.has(v)) {
    out.add(v);
  }

  const two = v.match(/^([a-z]{2})(?:-[a-z0-9]+)*$/i)?.[1]?.toLowerCase();
  if (two) {
    byIso6391!.get(two)?.forEach((c) => out.add(c));
  }

  const nameKey = normStr(String(name ?? ""));
  if (out.size === 0 && nameKey && zhNameToSubtag) {
    const sub = zhNameToSubtag.get(nameKey);
    if (sub) {
      byIso6391!.get(sub)?.forEach((c) => out.add(c));
    }
  }

  return out;
}
